import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import userGreyIcon from 'assets/images/icons/user-grey.svg';
import crossGreyIcon from 'assets/images/icons/cross-grey.svg';
import chevronGreyIcon from 'assets/images/icons/chevron-grey.svg';
import SingleCheckBoxField from 'components/form-components/SingleCheckBoxField';
import { useTranslation } from 'react-i18next';
import AlertModal from 'components/common-components/AlertModal';
import FormButton from 'components/form-components/FormButton';
import SelectedUnselectedMultiTabSelector from 'components/common-components/SelectedUnselectedMultiTabSelector';
import { CallAPI } from 'actions/General';
import MultiLevelSelect from 'components/form-components/MultiLevelSelect';
import toast from 'react-hot-toast';

const tabsConfig = [
  {
    key: 'users',
    label: 'text_users',
    icon: userGreyIcon,
    fetch: () => CallAPI('GET_ALL_SEARCH_ABLE_USERS'),
    mapData: (res) =>
      res.data.users.map((u) => ({
        ...u,
        id: u._id,
        name: `${u.firstName} ${u.lastName}`,
      })),
  },
  {
    key: 'guests',
    label: 'text_guests',
    icon: userGreyIcon,
    fetch: () => CallAPI('GET_ALL_DIRECTORY_USERS'),
    mapData: (res) =>
      res.data.list.map((u) => ({
        ...u,
        id: u._id,
        name: `${u.firstName} ${u.lastName}`,
      })),
  },
];

const AddRecipientsModal = ({ handleClose, setRecipientList, templateOptions }) => {
  const { t } = useTranslation();

  const [values, setValues] = useState({
    users: [],
    guests: [],
    template: null,
    channels: { email: false, sms: false, whatsapp: false },
  });
  const [allUsers, setAllUsers] = useState(null);

  const handleChange = (field, value) => {
    setValues((prevValues) => {
      const updatedValues = { ...prevValues };
      updatedValues[field] = value;
      return updatedValues;
    });
  };

  const handleCheckboxChange = useCallback((name) => {
    setValues((prev) => ({
      ...prev,
      channels: {
        ...prev.channels,
        [name]: !prev.channels[name],
      },
    }));
  }, []);

  const validate = () => {
    const hasRecipients = [...values.users, ...values.guests].length > 0;
    const hasChannel = Object.values(values.channels).some(Boolean);

    if (!hasRecipients) {
      toast.error("Please select recipient's");
      return false;
    }

    if (!hasChannel) {
      toast.error("Please select channel's");
      return false;
    }

    return true;
  };

  const handleSubmit = () => {
    if (!validate()) return;
    const mergedAllUsers = [...allUsers.users, ...allUsers.guests];
    const mergedSelectedUsers = [...values.users, ...values.guests];

    const filteredUsers = mergedAllUsers.filter((u) => mergedSelectedUsers.includes(u._id));
    const prepareTableData = filteredUsers.map((u) => ({
      ...u,
      name: u.firstName + ' ' + u.lastName,
      email: u.email || '',
      countryCode: u.countryCode || '',
      mobileNumber: u.mobileNumber || '',
      template: values.template,
      channels: Object.keys(values.channels).filter((key) => {
        if (!values.channels[key]) return false;
        if (key === 'email') return !!u.email;
        if (key === 'sms' || key === 'whatsapp') return !!u.mobileNumber;
        return false;
      }),
    }));
    setRecipientList((prev) => {
      const newUniqueUsers = prepareTableData.filter(
        (newItem) => !prev.some((existing) => existing._id === newItem._id),
      );

      return [...prev, ...newUniqueUsers];
    });
    handleClose();
  };

  return (
    <div>
      <AlertModal
        show={true}
        actionButtons={[]}
        noPadding
        size='md'
        backdropClassName={'backdrop-overlap'}
        enforceFocus={false}
      >
        <div className='w-100'>
          {/* Header */}
          <div className='p-3 d-flex justify-content-between border-bottom-grey-10-h-1'>
            <span className='theme-font-inter-semi-bold theme-size-1_2 theme-text-black-1'>
              {t('text_add_recipients')}
            </span>
            <img className='p-2 cursor-pointer' src={crossGreyIcon} onClick={handleClose} />
          </div>

          <div className='p-3 w-100 d-flex flex-column gap-3'>
            <div className='row align-items-center'>
              <span className='col-4'>{t('text_select_recipient')}</span>
              <div className='col-8'>
                <SelectedUnselectedMultiTabSelector
                  onDataLoad={(data) => {
                    setAllUsers(data);
                  }}
                  toggleComponent={
                    <div className='d-flex justify-content-between align-items-center border-grey-1-h-1 cursor-pointer rounded-sm p-2'>
                      <div>
                        <img src={userGreyIcon} />
                        <span className='theme-size-0_9 ms-2'>
                          {(() => {
                            const local = values?.users || [];
                            const guests = values?.guests || [];
                            const merged = [...local, ...guests];
                            if (!merged.length) return t('select_select');

                            return `${merged.length} users selected`;
                          })()}
                        </span>
                      </div>
                      <img src={chevronGreyIcon} className='rotate-180' />
                    </div>
                  }
                  existingSelections={{
                    users: values.users,
                    guests: values.guests,
                  }}
                  tabs={tabsConfig}
                  doneAction={(data) => {
                    setValues((prev) => ({ ...prev, ...data }));
                  }}
                />
              </div>
            </div>

            <div className='row align-items-center'>
              <span className='col-4'>{t('text_select_template')}</span>
              <div className='col-8'>
                <MultiLevelSelect
                  options={{ children: templateOptions }}
                  value={[]}
                  name='template'
                  handleChange={(name, value) => handleChange(name, value[0])}
                  toggleClasses='px-2 py-2 d-flex gap-2 justify-content-between align-items-center rounded-sm cursor-pointer border-grey-1-h-1'
                  toggleComponent={
                    <>
                      <label className='theme-size-0_9 cursor-pointer '>
                        {templateOptions.find((t) => t.value === values.template)?.displayValue ??
                          t('select_select')}
                      </label>
                      <img src={chevronGreyIcon} className='rotate-180' />
                    </>
                  }
                  fullWidth
                />
              </div>
            </div>

            <div className='row align-items-center'>
              <span className='col-4'>{t('text_select_sending_channel')}</span>
              <div className='col-8 d-flex gap-3 justify-content-end'>
                {['email', 'sms', 'whatsapp'].map((channel) => (
                  <SingleCheckBoxField
                    key={channel}
                    name={channel}
                    value={values.channels[channel]}
                    handleChange={() => handleCheckboxChange(channel)}
                    text={<span className='text-capitalize'>{t(`text_${channel}`)}</span>}
                    classes='width-max-content'
                  />
                ))}
              </div>
            </div>
          </div>

          <div className='p-3 d-flex justify-content-end border-top-grey-10-h-1'>
            <div className='d-flex gap-3'>
              <FormButton text='button_cancel' variant='green-2' onClick={handleClose} />
              <FormButton text='button_done' variant='green-1' onClick={handleSubmit} />
            </div>
          </div>
        </div>
      </AlertModal>
    </div>
  );
};

AddRecipientsModal.propTypes = {
  handleClose: PropTypes.func.isRequired,
  setRecipientList: PropTypes.func.isRequired,
  templateOptions: PropTypes.array.isRequired,
};

export default AddRecipientsModal;
